import {
  DownloadOutlined,
  LeftOutlined,
  LockOutlined,
  RightOutlined,
  SaveOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Alert, Card, Form, Input, InputNumber, message, Modal, Select, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { attendanceApi } from "api/attendance.api";
import { departmentApi } from "api/employee.api";
import { AppButton, AppTable } from "components/antd-ui";
import { TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { useClientPaging } from "hooks/useClientPaging";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AttendanceMonthlyData, AttendanceRow } from "types/attendance";
import { Department } from "types/employee";
import { formatVND, getTitle } from "utils";
import { can } from "utils/auth";
import { formatDateTime } from "utils/date";
import dayjs, { Dayjs } from "utils/dayjs";
import { exportExcel } from "utils/exportExcel";

const attendancePath = "/attendance";
const { TextArea } = Input;

// Tổng lương ở bảng chấm công = lương chấm công + HH kỹ thuật (số ở cột Hoa hồng) + tăng/giảm; HH SALE chưa cộng
const totalPayOf = (row: AttendanceRow) =>
  (row.salaryAmount || 0) + (row.commissionState === "KT" ? row.commissionAmount || 0 : 0) + (row.adjustAmount || 0);

const money = (value: number) => `${formatVND(Math.round(value || 0))}đ`;
const days = (value: number) => String(value).replace(".", ",");

const EMPTY_DATA: AttendanceMonthlyData = { isLocked: false, standardDays: 24, canViewSalary: false, rows: [] };

// leaveDays = nghỉ không lương (trừ lương), paidLeaveDays = nghỉ có phép (không trừ lương)
type Edit = { leaveDays: number; paidLeaveDays: number; adjustAmount: number; note: string };

export const AttendancePage = observer(({ title = "" }) => {
  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const [data, setData] = useState<AttendanceMonthlyData>(EMPTY_DATA);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  // Tab: nhập chấm công | bảng lương tổng hợp (lương chấm công + hoa hồng kỹ thuật)
  const [departments, setDepartments] = useState<Department[]>([]);
  // Thay đổi chưa lưu: employeeId -> ngày nghỉ + ghi chú
  const [edits, setEdits] = useState<Record<number, Edit>>({});

  const [unlockVisible, setUnlockVisible] = useState(false);
  const [unlockNote, setUnlockNote] = useState("");
  const [unlockLoading, setUnlockLoading] = useState(false);

  const canUpdate = can(attendancePath, "UPDATE");
  const canLock = can(attendancePath, "LOCK");
  const canUnlock = can(attendancePath, "UNLOCK");

  const month = monthValue.month() + 1;
  const year = monthValue.year();
  const { isLocked, standardDays, canViewSalary } = data;
  const dirty = Object.keys(edits).length > 0;
  const editable = canUpdate && !isLocked;

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    departmentApi.findAll().then((res) => setDepartments(res.data.departments || []));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceApi.findAll({ month, year });
      setData(res.data as AttendanceMonthlyData);
      setEdits({});
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Đổi tháng: hỏi trước nếu còn thay đổi chưa lưu
  const changeMonth = useCallback(
    (next: (prev: Dayjs) => Dayjs) => {
      const apply = () => setMonthValue(next);
      if (!dirty) {
        apply();
        return;
      }
      Modal.confirm({
        title: "Bỏ thay đổi chưa lưu?",
        content: "Bạn đã nhập ngày nghỉ nhưng chưa bấm Lưu. Chuyển tháng sẽ mất các thay đổi này.",
        okText: "Chuyển tháng",
        cancelText: "Ở lại",
        onOk: apply,
      });
    },
    [dirty],
  );

  // Dòng đã áp thay đổi chưa lưu và tính lại lương ngay trên giao diện
  const rows = useMemo<AttendanceRow[]>(
    () =>
      data.rows.map((row) => {
        const edit = edits[row.employeeId];
        if (!edit || isLocked) return row;
        const workDays = Math.max(0, standardDays - edit.leaveDays);
        return {
          ...row,
          leaveDays: edit.leaveDays,
          paidLeaveDays: edit.paidLeaveDays,
          adjustAmount: edit.adjustAmount,
          note: edit.note,
          workDays,
          salaryAmount:
            row.baseSalary !== undefined
              ? Math.round(((row.baseSalary / standardDays) * workDays * row.salaryRate) / 100)
              : row.salaryAmount,
        };
      }),
    [data.rows, edits, isLocked, standardDays],
  );

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!departmentId || row.departmentId === departmentId) &&
        (!keyword || row.employeeName.toLowerCase().includes(keyword) || row.employeeCode.toLowerCase().includes(keyword)),
    );
  }, [rows, search, departmentId]);

  const paging = useClientPaging(filteredRows, 50);

  const totalAmount = useMemo(() => filteredRows.reduce((sum, row) => sum + (row.salaryAmount || 0), 0), [filteredRows]);
  const totalLeave = useMemo(() => filteredRows.reduce((sum, row) => sum + row.leaveDays, 0), [filteredRows]);
  const totalPaidLeave = useMemo(() => filteredRows.reduce((sum, row) => sum + (row.paidLeaveDays || 0), 0), [filteredRows]);
  const totalAdjust = useMemo(() => filteredRows.reduce((sum, row) => sum + (row.adjustAmount || 0), 0), [filteredRows]);
  const totalPay = useMemo(() => filteredRows.reduce((sum, row) => sum + totalPayOf(row), 0), [filteredRows]);
  const totalCommission = useMemo(() => filteredRows.reduce((sum, row) => sum + (row.commissionAmount || 0), 0), [filteredRows]);

  const patchEdit = useCallback(
    (row: AttendanceRow, patch: Partial<Edit>) => {
      setEdits((prev) => {
        const current = prev[row.employeeId] || {
          leaveDays: row.leaveDays,
          paidLeaveDays: row.paidLeaveDays || 0,
          adjustAmount: row.adjustAmount || 0,
          note: row.note,
        };
        return { ...prev, [row.employeeId]: { ...current, ...patch } };
      });
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await attendanceApi.saveLeave({
        month,
        year,
        items: Object.entries(edits).map(([employeeId, edit]) => ({
          employeeId: Number(employeeId),
          leaveDays: edit.leaveDays,
          paidLeaveDays: edit.paidLeaveDays,
          adjustAmount: edit.adjustAmount,
          note: edit.note,
        })),
      });
      message.success("Lưu chấm công thành công");
      await fetchData();
    } finally {
      setSaving(false);
    }
  }, [edits, fetchData, month, year]);

  const handleLock = () => {
    // HH Chương: khoản Lương = lương cố định, khoản Hoa hồng = tổng lương trừ lương cố định (không âm)
    const fixed = data.chuongFixedSalary || 0;
    const chuongRow = canViewSalary ? data.rows.find((row) => row.technicalRole === "CHUONG") : undefined;
    const chuongTotal = chuongRow ? (chuongRow.salaryAmount || 0) + (chuongRow.technicalCommission || 0) + (chuongRow.adjustAmount || 0) : 0;
    const chuongCommission = Math.max(0, chuongTotal - fixed);
    const others = data.rows.filter((row) => row !== chuongRow);
    const salaryCount =
      (canViewSalary ? others.filter((row) => (row.salaryAmount || 0) + (row.adjustAmount || 0) > 0).length : others.length) +
      (chuongRow && fixed > 0 ? 1 : 0);
    const commissionCount = others.filter((row) => (row.technicalCommission || 0) > 0).length + (chuongCommission > 0 ? 1 : 0);
    const pending = data.technicalPending;
    Modal.confirm({
      title: `Chốt chấm công tháng ${monthValue.format("MM/YYYY")}?`,
      content: (
        <div>
          <div>Số nhân sự: {data.rows.length}</div>
          {canViewSalary && <div>Tổng lương chấm công: {money(data.totalAmount || 0)}</div>}
          {canViewSalary && !!data.totalAdjust && <div>Tổng tăng/giảm: {money(data.totalAdjust)}</div>}
          {canViewSalary && <div>Tổng hoa hồng kỹ thuật: {money(data.totalTechnical || 0)}</div>}
          {chuongRow && (
            <div className="mt-2">
              {chuongRow.employeeName} (HH Chương): khoản "Lương" {money(fixed)}, khoản "Hoa hồng" {money(chuongCommission)}
              {chuongTotal < fixed ? ` (tổng lương ${money(chuongTotal)} thấp hơn lương cố định nên chi lương đủ ${money(fixed)}, không có hoa hồng)` : ""}.
            </div>
          )}
          <div className="mt-2">
            Hệ thống sẽ tạo {salaryCount} khoản chi "Lương"{commissionCount > 0 ? ` và ${commissionCount} khoản chi "Hoa hồng"` : ""} ở
            menu Chi phí (mỗi nhân sự một khoản, trạng thái Chờ chi, ngày phát sinh là ngày chốt). Số liệu sau khi chốt không
            tự tính lại khi lương cơ bản thay đổi.
          </div>
          {!!pending?.debtCount && (
            <div className="mt-2">
              Hoa hồng kỹ thuật chỉ tính {pending.debtCount} đợt đã "Đã trả HH" có ngày trả trong tháng; các đợt này bị khoá sửa ở
              menu Hoa hồng kỹ thuật cho đến khi mở khoá lương tháng này. Đợt còn "Chưa trả HH" không được tính.
            </div>
          )}
        </div>
      ),
      okText: "Chốt tháng",
      cancelText: "Huỷ",
      onOk: async () => {
        await attendanceApi.lock({ month, year });
        message.success("Chốt chấm công thành công");
        await fetchData();
      },
    });
  };

  const handleUnlock = async () => {
    if (!unlockNote.trim()) {
      message.warning("Vui lòng nhập lý do mở khoá");
      return;
    }
    setUnlockLoading(true);
    try {
      await attendanceApi.unlock({ month, year, note: unlockNote.trim() });
      message.success("Mở khoá thành công");
      setUnlockVisible(false);
      setUnlockNote("");
      await fetchData();
    } finally {
      setUnlockLoading(false);
    }
  };

  const commissionText = (row: AttendanceRow) =>
    row.commissionState === "SALE_PENDING" ? "Chưa chốt" : row.commissionState === "NONE" || !row.commissionState ? "" : row.commissionAmount;

  const handleExport = () => {
    exportExcel(
      filteredRows.map((row) => ({
        "Mã nhân sự": row.employeeCode,
        "Họ tên": row.employeeName,
        "Phòng ban": row.departmentName,
        "Lương cơ bản": row.baseSalary ?? "",
        "Ngày công chuẩn": row.standardDays,
        "Nghỉ có phép": row.paidLeaveDays || 0,
        "Nghỉ không lương": row.leaveDays,
        "Ngày công thực tế": row.workDays,
        "Tỷ lệ lương (%)": row.salaryRate,
        "Lương chấm công": row.salaryAmount ?? "",
        "Tăng/giảm": row.adjustAmount ?? "",
        "Hoa hồng": commissionText(row),
        "Hoa hồng kỹ thuật": row.technicalCommission ?? "",
        "Tổng lương": totalPayOf(row),
        "Ghi chú": row.note,
      })),
      `Cham-cong-${monthValue.format("MM-YYYY")}`,
    );
  };

  const columns = useMemo<TableColumnsType<AttendanceRow>>(
    () => [
      { title: "Mã", dataIndex: "employeeCode", key: "code", width: 110, render: (v) => <TableContent>{v || "--"}</TableContent> },
      { title: "Họ tên", dataIndex: "employeeName", key: "name", width: 200, render: (v) => <span className="font-semibold whitespace-nowrap">{v || "--"}</span> },
      { title: "Phòng ban", dataIndex: "departmentName", key: "department", width: 160, render: (v) => <TableContent>{v || "--"}</TableContent> },
      ...(canViewSalary
        ? [
          {
            title: "Lương cơ bản",
            dataIndex: "baseSalary",
            key: "baseSalary",
            width: 150,
            align: "right" as const,
            render: (v: number) => <TableContent>{money(v)}</TableContent>,
          },
        ]
        : []),
      {
        title: (
          <Tooltip title="Nghỉ có phép: không bị trừ lương, chỉ để ghi nhận và tổng hợp">
            <span>Nghỉ có phép</span>
          </Tooltip>
        ),
        dataIndex: "paidLeaveDays",
        key: "paidLeaveDays",
        width: 130,
        align: "right",
        render: (v: number, row) =>
          editable ? (
            <InputNumber
              min={0}
              max={standardDays - (row.leaveDays || 0)}
              step={0.5}
              precision={1}
              value={v || 0}
              style={{ width: 90 }}
              onChange={(value) => patchEdit(row, { paidLeaveDays: Number(value) || 0 })}
            />
          ) : (
            <TableContent>{days(v || 0)}</TableContent>
          ),
      },
      {
        title: (
          <Tooltip title="Nghỉ không lương: bị trừ vào lương">
            <span>Nghỉ không lương</span>
          </Tooltip>
        ),
        dataIndex: "leaveDays",
        key: "leaveDays",
        width: 150,
        align: "right",
        render: (v: number, row) =>
          editable ? (
            <InputNumber
              min={0}
              max={standardDays - (row.paidLeaveDays || 0)}
              step={0.5}
              precision={1}
              value={v}
              style={{ width: 90 }}
              onChange={(value) => patchEdit(row, { leaveDays: Number(value) || 0 })}
            />
          ) : (
            <TableContent>{days(v)}</TableContent>
          ),
      },
      {
        title: (
          <Tooltip title="Ngày công thực tế / ngày công chuẩn. Thực tế đã trừ nghỉ không lương, không trừ nghỉ có phép">
            <span>Ngày công</span>
          </Tooltip>
        ),
        key: "workDays",
        width: 100,
        align: "right",
        render: (_, row) => <TableContent>{`${days(row.workDays)}/${days(row.standardDays)}`}</TableContent>,
      },
      ...(canViewSalary
        ? [
          {
            title: "Tỷ lệ lương",
            dataIndex: "salaryRate",
            key: "salaryRate",
            width: 140,
            align: "center" as const,
            render: (v: number) => (v < 100 ? <Tag color="blue">Thử việc {days(v)}%</Tag> : <TableContent>100%</TableContent>),
          },
          {
            title: "Lương chấm công",
            dataIndex: "salaryAmount",
            key: "salaryAmount",
            width: 170,
            align: "right" as const,
            render: (v: number) => <span className="font-semibold">{money(v)}</span>,
          },
          {
            title: (
              <Tooltip title="HH KT lấy theo menu HH Chương / HH Dung trong tháng; HH SALE lấy theo Hoa hồng đã chốt. Chỉ để xem, không cộng vào lương chấm công">
                <span>Hoa hồng</span>
              </Tooltip>
            ),
            key: "commission",
            width: 170,
            align: "right" as const,
            render: (_: unknown, row: AttendanceRow) =>
              row.commissionState === "SALE_PENDING" ? (
                <Tag>Chưa chốt</Tag>
              ) : row.commissionState === "NONE" || !row.commissionState ? (
                <TableContent>--</TableContent>
              ) : (
                <Tooltip title={row.commissionState === "KT" ? "HH kỹ thuật" : "HH SALE"}>
                  <span className="font-semibold">{money(row.commissionAmount || 0)}</span>
                </Tooltip>
              ),
          },
          {
            title: (
              <Tooltip title="Điều chỉnh tay khi có sự cố trong tháng: số dương là cộng, số âm là trừ. Cộng vào khoản chi Lương khi chốt; bắt buộc ghi lý do ở cột Ghi chú">
                <span>Tăng/giảm</span>
              </Tooltip>
            ),
            dataIndex: "adjustAmount",
            key: "adjustAmount",
            width: 170,
            align: "right" as const,
            render: (v: number, row: AttendanceRow) =>
              editable ? (
                <InputNumber
                  step={10000}
                  precision={0}
                  value={v || 0}
                  style={{ width: 140 }}
                  formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                  parser={(value) => Number(`${value ?? ""}`.replace(/\./g, "")) || 0}
                  onChange={(value) => patchEdit(row, { adjustAmount: Math.round(Number(value) || 0) })}
                />
              ) : v ? (
                <span className={v < 0 ? "text-red-600" : "text-green-700"}>{`${v > 0 ? "+" : ""}${money(v)}`}</span>
              ) : (
                <TableContent>--</TableContent>
              ),
          },
          {
            title: "Tổng lương",
            key: "totalPay",
            width: 160,
            align: "right" as const,
            render: (_: unknown, row: AttendanceRow) => <span className="font-semibold">{money(totalPayOf(row))}</span>,
          },
        ]
        : []),
      {
        title: "Ghi chú",
        dataIndex: "note",
        key: "note",
        width: 240,
        render: (v: string, row) =>
          editable ? (
            <Input value={v} maxLength={500} onChange={(event) => patchEdit(row, { note: event.target.value })} />
          ) : (
            <TableContent>{v || "--"}</TableContent>
          ),
      },
    ],
    [canViewSalary, editable, patchEdit, standardDays],
  );

  const missingRoles = !isLocked && canViewSalary ? data.technicalPending?.missingRoles || [] : [];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              <AppButton tone="light" aria-label="Tháng trước" onClick={() => changeMonth((prev) => prev.subtract(1, "month"))}>
                <LeftOutlined />
              </AppButton>
              <div className="min-w-[110px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
              <AppButton tone="light" aria-label="Tháng sau" onClick={() => changeMonth((prev) => prev.add(1, "month"))}>
                <RightOutlined />
              </AppButton>
            </div>

            <Tooltip
              title={
                isLocked
                  ? `Chốt bởi ${data.period?.lockedBy || "--"}${data.period?.lockedAt ? ` lúc ${formatDateTime(data.period.lockedAt)}` : ""}`
                  : "Tháng chưa chốt: lương tự tính theo lương cơ bản hiện tại"
              }
            >
              <Tag color={isLocked ? "default" : "orange"}>{isLocked ? "🔒 Đã chốt" : "⚡ Chưa chốt - tự tính"}</Tag>
            </Tooltip>

            <Input.Search
              allowClear
              placeholder="Tìm theo mã, tên nhân sự"
              style={{ width: 240 }}
              onSearch={setSearch}
              onChange={(event) => !event.target.value && setSearch("")}
            />
            <Select
              allowClear
              placeholder="Tất cả phòng ban"
              style={{ width: 200 }}
              value={departmentId}
              options={departments.map((department) => ({ label: department.name, value: department.id }))}
              onChange={(value) => setDepartmentId(value as number | undefined)}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {canViewSalary && (
              <AppButton tone="light" icon={<DownloadOutlined />} onClick={handleExport} disabled={!filteredRows.length}>
                Xuất Excel
              </AppButton>
            )}
            {editable && (
              <AppButton tone="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirty} onClick={handleSave}>
                Lưu{dirty ? ` (${Object.keys(edits).length})` : ""}
              </AppButton>
            )}
            {canLock && !isLocked && (
              <Tooltip
                title={
                  dirty
                    ? "Hãy lưu thay đổi trước khi chốt tháng"
                    : missingRoles.length
                      ? "Cần gán nhân sự nhận hoa hồng kỹ thuật trước khi chốt"
                      : ""
                }
              >
                <span>
                  <AppButton
                    icon={<LockOutlined />}
                    disabled={dirty || !data.rows.length || missingRoles.length > 0}
                    onClick={handleLock}
                  >
                    Chốt tháng
                  </AppButton>
                </span>
              </Tooltip>
            )}
            {canUnlock && isLocked && (
              <AppButton tone="light" icon={<UnlockOutlined />} onClick={() => setUnlockVisible(true)}>
                Mở khoá
              </AppButton>
            )}
          </div>
        </div>
      </Card>

      {missingRoles.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Tháng này có hoa hồng kỹ thuật đã trả cho ${missingRoles
            .map((role) => (role === "CHUONG" ? "HH Chương" : "HH Dung"))
            .join(" và ")} nhưng chưa có nhân sự nhận vai trò đó. Hãy gán "Hoa hồng kỹ thuật" trong hồ sơ nhân sự trước khi chốt tháng.`}
        />
      )}

      {!canViewSalary && (
        <div className="text-xs text-gray-400">Bạn chưa có quyền xem thông tin nhạy cảm nên không thấy các cột tiền lương.</div>
      )}

      <TableLayout
        table={
          <>
            <AppTable<AttendanceRow>
              rowKey="employeeId"
              loading={loading}
              pagination={false}
              scroll={{ x: 1900 }}
              dataSource={paging.pageRows}
              columns={columns}
              locale={{ emptyText: "Không có nhân sự nào trong tháng này" }}
            />
            <div className="flex flex-wrap items-center justify-end gap-6 px-4 py-3 font-semibold border-t border-gray-100">
              <span>Nhân sự: {filteredRows.length}</span>
              <span>Tổng nghỉ có phép: {days(totalPaidLeave)}</span>
              <span>Tổng nghỉ không lương: {days(totalLeave)}</span>
              {canViewSalary && <span>Tổng lương chấm công: {money(totalAmount)}</span>}
              {canViewSalary && <span>Tổng tăng/giảm: {money(totalAdjust)}</span>}
              {canViewSalary && <span>Tổng hoa hồng: {money(totalCommission)}</span>}
              {canViewSalary && <span>Tổng lương: {money(totalPay)}</span>}
            </div>
          </>
        }
        footer={
          <TableLayoutFooter
            showingFrom={paging.showingFrom}
            showingTo={paging.showingTo}
            total={paging.total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={paging.onChange}
          />
        }
      />

      <Modal
        open={unlockVisible}
        title="Mở khoá chấm công"
        okText="Xác nhận mở khoá"
        cancelText="Huỷ"
        confirmLoading={unlockLoading}
        onCancel={() => setUnlockVisible(false)}
        onOk={handleUnlock}
      >
        <div className="text-sm text-gray-500 mb-3">
          Mở khoá tháng {monthValue.format("MM/YYYY")} để sửa lại ngày nghỉ. Các khoản chi "Lương" và "Hoa hồng" đã sinh ở menu Chi
          phí sẽ bị gỡ, các đợt hoa hồng kỹ thuật được mở khoá sửa lại (trạng thái "Đã trả HH" giữ nguyên), và mọi thứ sinh lại khi chốt
          lần nữa. Không mở khoá được nếu đã có khoản lương/hoa hồng ở trạng thái Đã chi. Hành động này được ghi
          lại (ai mở khoá, lúc nào, lý do).
        </div>
        <Form layout="vertical">
          <Form.Item label={<>Lý do mở khoá <span className="text-red-500">*</span></>} required>
            <TextArea
              rows={3}
              value={unlockNote}
              onChange={(event) => setUnlockNote(event.target.value)}
              placeholder="VD: Nhập thiếu ngày nghỉ của nhân viên A..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});
